import { Link } from 'react-router-dom';
import {
  PhysicsIcon,
  ChemistryIcon,
  BiologyIcon,
  MathematicsIcon,
} from '../components/icons';
import styles from './Subjects.module.css';

const SUBJECTS = [
  {
    name: 'Physics',
    slug: 'physics',
    icon: PhysicsIcon,
    description: 'Classical mechanics, electromagnetism, thermodynamics, waves, optics, and modern physics. All simulations solve actual differential equations with real physical constants.',
    scope: [
      'Kinematics and dynamics in 1D, 2D, and 3D',
      'Conservation laws (energy, momentum, angular momentum)',
      'Rotational mechanics and oscillations',
      'Electrostatics and magnetostatics',
      'Circuit analysis with transient response',
      'Thermodynamic processes and heat transfer',
      'Wave interference and diffraction',
      'Geometric and physical optics',
    ],
    experiments: 47,
    domains: ['mechanics', 'electricity', 'thermodynamics', 'waves', 'optics', 'modern'],
  },
  {
    name: 'Chemistry',
    slug: 'chemistry',
    icon: ChemistryIcon,
    description: 'Reaction kinetics, equilibrium, thermochemistry, electrochemistry, and stoichiometry. Deterministic simulations of chemical processes with accurate rate laws.',
    scope: [
      'Stoichiometric calculations and limiting reagents',
      'Gas laws and ideal vs real behavior',
      'Thermochemistry and Hess\'s law applications',
      'Chemical equilibrium and Le Chatelier\'s principle',
      'Acid-base equilibria and pH calculations',
      'Reaction kinetics and rate mechanisms',
      'Electrochemistry and redox potentials',
      'Solubility and precipitation equilibria',
    ],
    experiments: 38,
    domains: ['stoichiometry', 'equilibrium', 'kinetics', 'thermochemistry', 'electrochemistry'],
  },
  {
    name: 'Biology',
    slug: 'biology',
    icon: BiologyIcon,
    description: 'Population dynamics, enzyme kinetics, diffusion processes, ecological systems, and physiology. Model-driven simulations grounded in biological principles.',
    scope: [
      'Population growth and logistic models',
      'Predator-prey dynamics',
      'Enzyme kinetics (Michaelis-Menten)',
      'Membrane transport and diffusion',
      'Osmosis and tonicity',
      'Cardiovascular system modeling',
      'Respiratory gas exchange',
      'Ecological succession and competition',
    ],
    experiments: 32,
    domains: ['ecology', 'physiology', 'cell-biology', 'genetics', 'evolution'],
  },
  {
    name: 'Mathematics',
    slug: 'mathematics',
    icon: MathematicsIcon,
    description: 'Calculus, differential equations, numerical analysis, linear algebra, and optimization. Visualize and explore mathematical concepts through interactive computation.',
    scope: [
      'Limits and continuity visualization',
      'Derivative as instantaneous rate of change',
      'Riemann sums and integration',
      'Differential equation solvers',
      'Vector fields and gradient visualization',
      'Matrix operations and transformations',
      'Eigenvalue problems and applications',
      'Optimization and root-finding methods',
    ],
    experiments: 41,
    domains: ['calculus', 'algebra', 'differential-equations', 'statistics', 'numerical-analysis'],
  },
];

export function Subjects() {
  return (
    <div className={styles.subjects}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <Link to="/" className={styles.backLink}>← Back to Home</Link>
          <h1 className={styles.title}>Subjects</h1>
          <p className={styles.subtitle}>
            AxLabs covers four core scientific disciplines. Each subject area contains
            experiments that progress from foundational concepts to advanced applications.
          </p>
        </div>
      </header>

      {/* Overview */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <div className={styles.prose}>
            <p className={styles.proseText}>
              All subjects share a common design philosophy: deterministic computation, real governing equations,
              and reproducible outputs. The differences lie in the phenomena being modeled, not the underlying
              computational approach.
            </p>
            <p className={styles.proseText}>
              Each experiment includes theoretical background, interactive simulation, data analysis tools,
              and assessment-ready outputs. Students can export data, compare results across parameter sets,
              and validate their understanding against theoretical predictions.
            </p>
          </div>
        </div>
      </section>

      {/* Subjects Grid */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Available Subjects</h2>
          <div className={styles.subjectsGrid}>
            {SUBJECTS.map((subject) => {
              const IconComponent = subject.icon;
              return (
                <Link key={subject.slug} to={`/subjects/${subject.slug}`} className={styles.subjectCard}>
                  <div className={styles.subjectHeader}>
                    <div className={styles.subjectIcon}>
                      <IconComponent size={40} />
                    </div>
                    <div className={styles.subjectMeta}>
                      <h3 className={styles.subjectName}>{subject.name}</h3>
                      <span className={styles.subjectCount}>{subject.experiments} experiments</span>
                    </div>
                  </div>
                  <p className={styles.subjectDesc}>{subject.description}</p>
                  <div className={styles.subjectFooter}>
                    <span className={styles.subjectLink}>View {subject.name.toLowerCase()} →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Scope Comparison */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Scope by Subject</h2>
          <div className={styles.scopeGrid}>
            {SUBJECTS.map((subject) => {
              const IconComponent = subject.icon;
              return (
                <div key={subject.slug} className={styles.scopeCard}>
                  <div className={styles.scopeHeader}>
                    <IconComponent size={20} />
                    <h3 className={styles.scopeTitle}>{subject.name}</h3>
                  </div>
                  <ul className={styles.scopeList}>
                    {subject.scope.map((item) => (
                      <li key={item} className={styles.scopeItem}>{item}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cross-Subject Connections */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Cross-Subject Connections</h2>
          <div className={styles.connectionsGrid}>
            <div className={styles.connectionCard}>
              <h3 className={styles.connectionTitle}>Physics ↔ Chemistry</h3>
              <p className={styles.connectionText}>
                Thermodynamics bridges both subjects. Energy transfer, molecular motion, and state changes
                appear in both physics and chemistry contexts with different emphases.
              </p>
            </div>
            <div className={styles.connectionCard}>
              <h3 className={styles.connectionTitle}>Biology ↔ Chemistry</h3>
              <p className={styles.connectionText}>
                Biochemistry fundamentals. Enzyme kinetics, membrane transport, and metabolic pathways
                apply chemical principles to biological systems.
              </p>
            </div>
            <div className={styles.connectionCard}>
              <h3 className={styles.connectionTitle}>Mathematics ↔ All Sciences</h3>
              <p className={styles.connectionText}>
                Differential equations model dynamics across physics, chemistry, and biology.
                Calculus underlies rate laws, optimization appears everywhere.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <Link to="/curriculum" className={styles.footerLink}>View Full Curriculum</Link>
          <span className={styles.footerSeparator}>·</span>
          <a href="https://github.com/staticpayload/axlabs" className={styles.footerLink} target="_blank" rel="noopener noreferrer">GitHub</a>
        </div>
      </footer>
    </div>
  );
}
